import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, useToast } from "@chakra-ui/react";
import Header from "components/Header";
import CreateButton from "components/CreateButton";
import CreateModal from "./CreateModal";
import { CMSModels, CMSModel } from "./CMSModels";
import { legacyGet } from "api";
import DataTable from "./DataTable";
import { useAuthContext } from "providers/AuthProvider/AuthProvider";

//Some names are not user-friendly
const userFriendlyTitle = {
  tap: "Tapping Chart",
  "9g": "9G Steps",
};
const Model: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const { model } = useParams<{ model: string }>();
  const MODEL = CMSModels[model];

  const [modelCategories, setModelCategories] = useState<any>([]);
  const toast = useToast();

  //the state for tracking the records
  const [records, setRecords] = useState<CMSModel[]>([]);
  const { user } = useAuthContext();

  //method to fetch the data for the current model
  const fetchRecords = useCallback(async () => {
    if (!user?.accessToken) {
      return;
    }

    setLoading(true);
    const response = await legacyGet<Array<CMSModel>>(
      MODEL.getEndpoint,
      undefined,
      user.accessToken
    ).catch(() => {
      toast({
        title: "Failed to load data..",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return null;
    });

    if (response && Array.isArray(response)) {
      for (const entry of response) {
        if (entry.name) {
          entry.name =
            userFriendlyTitle[entry.name as keyof typeof userFriendlyTitle] ||
            entry.name;
        }
        // Handle null dates for terms and conditions
        if (MODEL.getEndpoint === "terms-and-conditions") {
          entry.activationDate = entry.activationDate || "";
          entry.expirationDate = entry.expirationDate || "";
          entry.createdAt = entry.createdAt?.split("T")[0] || "";
        }
      }
      setRecords(response);
    } else if (response !== null) {
      toast({
        title: "Failed to load data..",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setRecords([]);
    }
    setLoading(false);
  }, [MODEL.getEndpoint, toast, user?.accessToken]);

  const getModelCategories = useCallback(async () => {
    if (!user?.accessToken || !MODEL.getCategoryEndpoint) {
      return;
    }

    const response = await legacyGet(
      MODEL.getCategoryEndpoint,
      undefined,
      user.accessToken
    ).catch(() => null);

    if (response && Array.isArray(response)) {
      setModelCategories(response);
    } else {
      toast({
        title: "Failed to load categories..",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [MODEL.getCategoryEndpoint, toast, user?.accessToken]);

  useEffect(() => {
    const hasStoredAuth = Boolean(window.localStorage.getItem("__auth"));

    if (!user?.accessToken || !MODEL) {
      setLoading(hasStoredAuth);
      return;
    }

    fetchRecords();
    getModelCategories();

    return () => {
      setRecords([]);
    };
  }, [MODEL, fetchRecords, getModelCategories, user?.accessToken]);

  if (!MODEL) {
    return (
      <div>
        <p>No Model Found</p>
      </div>
    );
  }
  if (loading) {
    return <div>Loading...</div>;
  }
  return (
    <>
      <Box padding="10px 0">
        <Header
          title={model.includes("_") ? model.replace("_", " ") : model}
          actions={
            <>
              <CreateModal
                title={MODEL.title}
                btnProps={{ color: "success", title: "Create" }}
                actions={<CreateButton />}
                labels={MODEL.requiredFields}
                postEndpoint={MODEL.postEndpoint}
                refetchRecords={fetchRecords}
                populated={MODEL.populated}
                records={records}
              />
            </>
          }
        />
      </Box>
      <DataTable
        fields={
          MODEL.common
            ? [...MODEL.displayFields, { key: "Content" }, { key: "Delete" }]
            : MODEL.postEndpoint === "pure-tone"
            ? [
                ...MODEL.displayFields,
                { key: "ArContent" },
                { key: "EnContent" },
                { key: "Edit" },
                { key: "Delete" },
              ]
            : [
                ...MODEL.displayFields,
                { key: "ArContent" },
                { key: "EnContent" },
                { key: "Delete" },
              ]
        }
        items={records}
        inputFields={MODEL.fields}
        putEndpoint={MODEL.putEndpoint}
        deleteEndpoint={MODEL.deleteEndpoint}
        categories={modelCategories}
        updateCategoryEndpoint={MODEL.updateCategoryEndpoint}
        updateStatusEndpoint={MODEL.updateStatusEndpoint}
        refetchRecords={fetchRecords}
        populated={MODEL.populated}
        putMethod={MODEL.putMethod}
        postEditEndpoint={MODEL.postEndpoint}
        common={MODEL.common}
        requiredFields={MODEL.requiredFields}
        loading={loading}
      />
    </>
  );
};

export default Model;
